import axios, {
  AxiosRequestConfig,
  AxiosError,
  Method,
  AxiosHeaders,
} from "axios";
import { myLocalStorage } from "../components/StorageHelper";
import { checkIfTokenExpired } from "./tokenHelpers";
import {
  attachAbortTracking,
  enqueueRefreshWaiter,
  handleInvalidSessionLogout,
  isSessionRefreshing,
  refreshSessionTokens,
  waitForActiveRefresh,
} from "./sessionTokenManager";

export interface ApiRequestOptions {
  method: Method;
  url: string;
  data?: any;
  params?: Record<string, any>;
  headers?: Record<string, string>;
  timeout?: number;
  signal?: AbortSignal;
}

const api = axios.create({});
const nativeAxiosRequest = axios.request.bind(axios);

const DEFAULT_API_TIMEOUT_MS = 30_000;

api.interceptors.request.use(
  async (request) => {
    await waitForActiveRefresh();

    attachAbortTracking(request);

    const access_token = myLocalStorage.getItem("access_token");
    if (access_token) {
      request.headers!["Authorization"] = `Bearer ${access_token}`;
    }
    request.headers!["Referrer-Policy"] = "strict-origin-when-cross-origin";
    return request;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    if (originalRequest?.url?.includes("/logout")) {
      return Promise.reject(error);
    }

    if (!error.response || error.response.status !== 401) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      return Promise.reject(error);
    }
    originalRequest._retry = true;

    const stored_refresh_token = myLocalStorage.getItem("refresh_token");

    if (!stored_refresh_token || checkIfTokenExpired(stored_refresh_token)) {
      await handleInvalidSessionLogout();
      return Promise.reject(error);
    }

    if (isSessionRefreshing()) {
      return new Promise((resolve, reject) => {
        enqueueRefreshWaiter(
          (token: string) => {
            // Keep _retry true so a second 401 rejects instead of refreshing again.
            const retryConfig = { ...originalRequest, _retry: true };
            retryConfig.headers = {
              ...originalRequest.headers,
              Authorization: `Bearer ${token}`,
            };
            resolve(api(retryConfig));
          },
          reject,
        );
      });
    }

    try {
      const access_token = await refreshSessionTokens({
        cancelPending: false,
        logoutOnFailure: true,
      });
      // Keep _retry true so a second 401 rejects instead of refreshing again.
      const retryConfig = { ...originalRequest, _retry: true };
      retryConfig.headers = {
        ...originalRequest.headers,
        Authorization: `Bearer ${access_token}`,
      };
      return api(retryConfig);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  },
);

export async function apiClient<T = any>(options: ApiRequestOptions): Promise<T> {
  const config: AxiosRequestConfig = {
    method: options.method,
    url: options.url,
    data: options.data,
    params: options.params,
    headers: options.headers || {},
    timeout: options.timeout ?? DEFAULT_API_TIMEOUT_MS,
    signal: options.signal,
  };

  if (options.url.includes("/logout")) {
    const access_token = myLocalStorage.getItem("access_token");
    const headers: Record<string, string> = {
      ...(options.headers || {}),
    };
    if (access_token && !headers.Authorization) {
      headers.Authorization = `Bearer ${access_token}`;
    }
    return nativeAxiosRequest({ ...config, headers }).then((res) => res.data);
  }

  try {
    const response = await api(config);
    return response.data;
  } catch (error: any) {
    if (error?.code === "ERR_CANCELED") {
      throw error;
    }
    console.error("API Error:", error);
    throw error.response?.data || error.message || "Unknown API Error";
  }
}

const METHODS: Method[] = ["get", "post", "put", "patch", "delete", "head", "options"];

METHODS.forEach((method) => {
  // @ts-ignore
  axios[method] = async (url: string, dataOrConfig?: any, config?: any) => {
    const isGetOrDelete = method === "get" || method === "delete";
    const data = isGetOrDelete ? undefined : dataOrConfig;
    const finalConfig = isGetOrDelete ? dataOrConfig : config;

    const requestOptions: ApiRequestOptions = {
      method: method.toUpperCase() as Method,
      url,
      data,
      params: finalConfig?.params,
      headers: finalConfig?.headers,
      timeout: finalConfig?.timeout,
      signal: finalConfig?.signal,
    };

    return api({
      method: requestOptions.method,
      url: requestOptions.url,
      data: requestOptions.data,
      params: requestOptions.params,
      headers: requestOptions.headers,
      timeout: requestOptions.timeout,
      signal: requestOptions.signal,
    });
  };
});

axios.request = async (config) => {
  let plainHeaders: Record<string, string> | undefined;

  if (config.headers instanceof AxiosHeaders) {
    plainHeaders = config.headers.toJSON() as Record<string, string>;
  } else if (typeof config.headers === "object" && config.headers !== null) {
    plainHeaders = config.headers as Record<string, string>;
  }

  return api({
    method: config.method!.toUpperCase() as Method,
    url: config.url!,
    data: config.data,
    params: config.params,
    headers: plainHeaders,
    timeout: config.timeout,
    signal: config.signal,
  });
};

export default api;
